/**
 * nextStep.js - Paul's next-step rule (T-03 / T-04 / T-05) as pure helpers.
 *
 * Every lead and every open opportunity must always have at least one open
 * task - except a lead that has an open opportunity: the opportunity's task
 * is its next step, so it needs no second one of its own (Paul, 10/16/26):
 *   - a lead is a board unit (a site, or an account with no sites - Paul Q12:
 *     tasks never sit on a company that has sites) whose Designation is Lead
 *     and whose Status is Active or Cold, and that was not made automatically
 *     for an opportunity (auto_created, 10/12/26) - so an Account (designation
 *     Account, 10/21/26) never needs a lead task;
 *   - an open opportunity is a bid with status Open (Budget was folded into
 *     Open on 9/29/26). Won projects are PM work with their own task list
 *     (Paul Q10) and are exempt.
 *
 * Completing the LAST open task on one of those asks "What's the next step?"
 * (T-04), and the Dashboard lists every one that has none (T-05). Both read
 * the rule from here so the prompt and the list can never disagree.
 *
 * Tasks are crm_actions rows (snake_case, straight from the API). Their home
 * is entity_type + entity_id: 'account' = a lead site or account, 'project' =
 * an opportunity/project row, 'opportunity' = the same row from the retired
 * "Action items" panel (legacy, counted as the row's tasks). A task with
 * task_type 'check-in' is the automatic 90-day Customer check-in (T-08): it
 * sits on a site like a lead task but is its own type.
 */
import { designationOf, leadStatusOf, isBoardUnit, isAutoCreated, isLeftCompany, pickField } from '../../data/crmStages';
import { isProjectRow } from '../../data/projectStatus';

/** Opportunity statuses that still need a next step. */
export const OPEN_OPP_STATUSES = ['Open'];
/** Paul Q5: Lost = lost to someone, Cancelled = cancelled or not awarded, No Quote = we backed out.
 *  Q-01 (10/1/26): the opportunity status Dead was renamed Cancelled; Dead is a
 *  lead / site Status only (shut down), as in LEAD_EXIT_STATUSES below. */
export const CLOSED_OPP_STATUSES = ['Lost', 'Cancelled', 'No Quote'];
/** The next-step prompt's way out for an opportunity: close it. */
export const OPP_CLOSE_CHOICES = ['Won', ...CLOSED_OPP_STATUSES];
/** Lead statuses that need an open task (T-03), and the prompt's way out for a lead. */
export const LEAD_STATUSES_NEEDING_TASK = ['Active', 'Cold'];
export const LEAD_EXIT_STATUSES = ['Inactive', 'Dead'];
/** entity_types whose home is an opportunity/project row. */
export const PROJECT_TASK_TYPES = ['project', 'opportunity'];
/** crm_actions.task_type of the automatic Customer check-in (T-08). */
export const CHECK_IN_TASK_TYPE = 'check-in';

const lc = (s) =>
  String(s == null ? '' : s)
    .trim()
    .toLowerCase();
const inList = (list, v) => list.some((x) => lc(x) === lc(v));

export const indexById = (rows) => {
  const m = new Map();
  for (const r of rows || []) if (r && r.id != null) m.set(String(r.id), r);
  return m;
};

/** A lead that must always have an open task. An account made automatically
 *  for an opportunity (auto_created) is not a lead until someone tracks it. */
export const isLeadNeedingTask = (a) =>
  !!a && isBoardUnit(a) && !isAutoCreated(a) && designationOf(a) === 'Lead' && inList(LEAD_STATUSES_NEEDING_TASK, leadStatusOf(a));

/** An opportunity that must always have an open task (Open, never a won project). */
export const isOpenOpportunity = (p) => !!p && !isProjectRow(p) && inList(OPEN_OPP_STATUSES, p.status);

/** Ids of the sites / accounts that have an open opportunity (its task covers the lead). */
export const accountsWithOpenOpp = (projects) =>
  new Set(
    (projects || [])
      .filter(isOpenOpportunity)
      .map((p) => String(pickField(p, 'accountId', 'account_id') || ''))
      .filter(Boolean)
  );

/** One key per home, so a legacy 'opportunity' task and a 'project' task on the same row count together. */
export const homeKeyOf = (entityType, entityId) => {
  const kind = entityType === 'account' ? 'account' : PROJECT_TASK_TYPES.includes(entityType) ? 'project' : entityType;
  return `${kind}:${entityId}`;
};
export const taskHomeKey = (t) => homeKeyOf(t && t.entity_type, t && t.entity_id);

/**
 * Which Dashboard list a task belongs in: 'lead' (on an account or site),
 * 'check-in' (the automatic Customer check-in, T-08), 'opportunity' (on a bid
 * that is not won), 'project' (on a won project - the separate Project tasks
 * list) or 'other'. A task whose row is not loaded stays an opportunity task,
 * so it is never silently dropped from both lists.
 */
export function taskKind(t, projectsById) {
  if (!t) return 'other';
  if (t.task_type === CHECK_IN_TASK_TYPE) return 'check-in';
  if (t.entity_type === 'account') return 'lead';
  if (PROJECT_TASK_TYPES.includes(t.entity_type)) {
    const p = projectsById && projectsById.get(String(t.entity_id));
    return p && isProjectRow(p) ? 'project' : 'opportunity';
  }
  return 'other';
}

/**
 * "Company - Site" for a site, the plain name otherwise. Many sites are
 * already named "Karma - Irvine"; when the site name starts with the
 * company's first word it already says which company, so it is left alone.
 */
export function accountLabel(a, accountsById) {
  if (!a) return '';
  const name = a.name || '';
  const parentId = pickField(a, 'parentAccountId', 'parent_account_id');
  if (!parentId) return name;
  const parent = accountsById && accountsById.get(String(parentId));
  const company = String((parent && parent.name) || pickField(a, 'parentName', 'parent_name') || '').trim();
  if (!company) return name;
  const first = lc(company).split(/[\s,.-]+/)[0] || '';
  if (first.length >= 3 && lc(name).startsWith(first)) return name;
  return `${company} - ${name}`;
}

/** Same label the opportunity editor gives its tasks: "Q5365 · Name". */
export function opportunityLabel(p) {
  if (!p) return '';
  const q = p.quoteLetter || p.quote_letter;
  const name = p.name || p.topic || p.description || p.customer || p.id;
  return `${q ? `Q${q} · ` : ''}${name}`;
}

/**
 * Options for the opportunity's "Site / account" picker (A-02): every site
 * and every account without sites. A company that has sites is not offered -
 * an opportunity belongs to the exact plant (Paul Q2).
 */
export function siteOptions(accounts) {
  const byId = indexById(accounts);
  return (accounts || [])
    .filter((a) => a && a.id != null && isBoardUnit(a))
    .map((a) => ({ id: String(a.id), label: accountLabel(a, byId) }))
    .sort((x, y) => x.label.localeCompare(y.label));
}

/* ── contacts (T-02) ─────────────────────────────────────────────────────── */

// GET /api/accounts gives a company its own contacts PLUS every site's
// (tagged site_id); a site gets only its own. "Corporate" = untagged.
const isCorporate = (c) => !pickField(c, 'siteId', 'site_id');

/** C-04: the contacts a picker may offer - never one who has left the company. */
export const pickableContacts = (list) => (list || []).filter((c) => c && !isLeftCompany(c));

/**
 * The contacts a task on this account/site can reference: its own, plus - for
 * a site - its company's corporate contacts. Primary contacts first. A contact
 * marked Left company is never offered (C-04); contactIndex below still finds
 * them, so a task that already names one keeps showing the name.
 */
export function contactsForAccount(accountId, accountsById) {
  const a = accountId && accountsById ? accountsById.get(String(accountId)) : null;
  if (!a) return [];
  const own = pickableContacts(a.contacts).filter(isCorporate);
  const parentId = pickField(a, 'parentAccountId', 'parent_account_id');
  const parent = parentId ? accountsById.get(String(parentId)) : null;
  const corporate = parent
    ? pickableContacts(parent.contacts)
        .filter(isCorporate)
        .map((c) => ({ ...c, fromCompany: parent.name || true }))
    : [];
  const seen = new Set();
  const out = [];
  for (const c of [...own, ...corporate]) {
    if (!c || c.id == null || seen.has(String(c.id))) continue;
    seen.add(String(c.id));
    out.push(c);
  }
  // primary first, then the site's own before the company's, then by name
  const primary = (c) => (pickField(c, 'isPrimary', 'is_primary') ? 0 : 1);
  const origin = (c) => (c.fromCompany ? 1 : 0);
  return out.sort((x, y) => primary(x) - primary(y) || origin(x) - origin(y) || String(x.name || '').localeCompare(String(y.name || '')));
}

/** Contacts for a task's home: the account's, or the linked site's for an opportunity. */
export function contactsForHome(entityType, entityId, accountsById, projectsById) {
  if (entityType === 'account') return contactsForAccount(entityId, accountsById);
  if (PROJECT_TASK_TYPES.includes(entityType)) {
    const p = projectsById && projectsById.get(String(entityId));
    return contactsForAccount(p && pickField(p, 'accountId', 'account_id'), accountsById);
  }
  return [];
}

/** Every contact by id (Left company ones included), for showing a task's contact in a list. */
export function contactIndex(accounts) {
  const m = new Map();
  for (const a of accounts || [])
    for (const c of (a && a.contacts) || []) if (c && c.id != null && !m.has(String(c.id))) m.set(String(c.id), c);
  return m;
}

export function contactLabel(c) {
  if (!c) return '';
  const role = pickField(c, 'contactRole', 'contact_role') || c.role;
  const where = c.fromCompany ? ' (company)' : '';
  return `${c.name || 'Unnamed'}${role ? ` · ${role}` : ''}${where}`;
}

/* ── the rule ────────────────────────────────────────────────────────────── */

/**
 * The record a task lives on: { kind: 'lead' | 'opportunity' | 'project', id,
 * label, record }, or null when that record is not loaded.
 */
export function homeOfTask(t, accountsById, projectsById) {
  if (!t) return null;
  if (t.entity_type === 'account') {
    const a = accountsById && accountsById.get(String(t.entity_id));
    return a ? { kind: 'lead', id: String(a.id), label: accountLabel(a, accountsById), record: a } : null;
  }
  if (PROJECT_TASK_TYPES.includes(t.entity_type)) {
    const p = projectsById && projectsById.get(String(t.entity_id));
    return p ? { kind: isProjectRow(p) ? 'project' : 'opportunity', id: String(p.id), label: opportunityLabel(p), record: p } : null;
  }
  return null;
}

/** Does this home fall under the rule (an Active/Cold lead with no open
 *  opportunity, or an open opportunity)? `projectsById` lets a lead's open
 *  opportunity cover it. */
export const homeNeedsTask = (home, projectsById) =>
  !!home &&
  (home.kind === 'lead'
    ? isLeadNeedingTask(home.record) && !accountsWithOpenOpp(projectsById ? [...projectsById.values()] : []).has(String(home.record.id))
    : home.kind === 'opportunity'
      ? isOpenOpportunity(home.record)
      : false);

/** True when no OTHER open task shares this task's home. */
export function isLastOpenTask(task, tasks) {
  if (!task) return false;
  const key = taskHomeKey(task);
  return !(tasks || []).some((x) => x && x.id !== task.id && x.status === 'open' && taskHomeKey(x) === key);
}

/**
 * T-04: should completing `task` ask "What's the next step?" Returns the home
 * when it should, null when it should just complete. `homeTasks` must be the
 * home's CURRENT tasks from the server - a filtered list (My tasks shows only
 * mine) cannot tell whether someone else still has one open.
 */
export function nextStepHome(task, homeTasks, accountsById, projectsById) {
  if (!task || task.status !== 'open') return null;
  const home = homeOfTask(task, accountsById, projectsById);
  if (!homeNeedsTask(home, projectsById)) return null;
  return isLastOpenTask(task, homeTasks) ? home : null;
}

const byLabel = (x, y) => x.label.localeCompare(y.label);
const quoteDay = (p) => String(p.quoteDate || p.quote_date || '').slice(0, 10);

/**
 * T-05: every lead and open opportunity with NO open task. `openTasks` is the
 * open task list (any non-open rows in it are ignored).
 * Returns { leads, opportunities }: leads by name, opportunities newest first.
 */
export function recordsWithoutNextStep(accounts, projects, openTasks) {
  const covered = new Set((openTasks || []).filter((t) => t && (t.status == null || t.status === 'open')).map(taskHomeKey));
  const accountsById = indexById(accounts);
  const withOpp = accountsWithOpenOpp(projects);
  const leads = (accounts || [])
    .filter((a) => isLeadNeedingTask(a) && !covered.has(homeKeyOf('account', a.id)) && !withOpp.has(String(a.id)))
    .map((a) => ({ id: String(a.id), label: accountLabel(a, accountsById), owner: a.owner || '', status: leadStatusOf(a), record: a }))
    .sort(byLabel);
  const opportunities = (projects || [])
    .filter((p) => isOpenOpportunity(p) && !covered.has(homeKeyOf('project', p.id)))
    .map((p) => ({
      id: String(p.id),
      label: opportunityLabel(p),
      customer: p.customer || '',
      owner: p.owner || '',
      status: p.status,
      record: p,
    }))
    .sort((x, y) => quoteDay(y.record).localeCompare(quoteDay(x.record)) || byLabel(x, y));
  return { leads, opportunities };
}

/* ── links and ordering ──────────────────────────────────────────────────── */

export const leadHash = (id) => `#/leads?account=${encodeURIComponent(id)}`;
export const opportunityHash = (id) => `#/opportunities?project=${encodeURIComponent(id)}`;
export const projectHash = (id) => `#/projects?project=${encodeURIComponent(id)}`;
/** C-01: a contact's own page, opened from the Accounts tab. */
export const contactHash = (id) => `#/crm?contact=${encodeURIComponent(id)}`;

/** Where clicking a task on the Dashboard goes, or null. A check-in opens its site. */
export function taskHash(t, projectsById) {
  const kind = taskKind(t, projectsById);
  if (kind === 'lead' || kind === 'check-in') return leadHash(t.entity_id);
  if (kind === 'opportunity') return opportunityHash(t.entity_id);
  if (kind === 'project') return projectHash(t.entity_id);
  return null;
}

export const dueDay = (t) => String((t && t.due_date) || '').slice(0, 10);
export const isTaskOverdue = (t, today) => !!dueDay(t) && dueDay(t) < today;

/** Overdue first, then by due date; undated last. */
export function sortOverdueFirst(tasks, today) {
  const rank = (t) => (isTaskOverdue(t, today) ? 0 : dueDay(t) ? 1 : 2);
  return [...(tasks || [])].sort((a, b) => rank(a) - rank(b) || dueDay(a).localeCompare(dueDay(b)));
}
